import { normalizeDegrees } from "../core/astro";
import type { SkyObjectPosition } from "../core/types";
import type { SkyViewportState } from "./skyCanvas";

const MinFieldOfViewDeg = 36;
const MaxFieldOfViewDeg = 132;
const MinCenterAltitudeDeg = -18;
const MaxCenterAltitudeDeg = 88;

export const DefaultViewport: SkyViewportState = {
  centerAzimuthDeg: 180,
  centerAltitudeDeg: 35,
  fieldOfViewDeg: 105,
};

export function panViewport(
  viewport: SkyViewportState,
  deltaX: number,
  deltaY: number,
  width: number,
  height: number,
): SkyViewportState {
  const referenceSize = Math.max(260, Math.min(width, height));
  const degreesPerPixel = viewport.fieldOfViewDeg / referenceSize;

  return normalizeViewport({
    ...viewport,
    centerAzimuthDeg: viewport.centerAzimuthDeg - deltaX * degreesPerPixel,
    centerAltitudeDeg: viewport.centerAltitudeDeg + deltaY * degreesPerPixel,
  });
}

export function zoomViewport(viewport: SkyViewportState, zoomIn: boolean): SkyViewportState {
  return normalizeViewport({
    ...viewport,
    fieldOfViewDeg: viewport.fieldOfViewDeg * (zoomIn ? 0.86 : 1.14),
  });
}

export function targetViewportForObject(
  viewport: SkyViewportState,
  object: SkyObjectPosition,
): SkyViewportState {
  return normalizeViewport({
    centerAzimuthDeg: object.azimuthDeg,
    centerAltitudeDeg: object.altitudeDeg,
    fieldOfViewDeg: Math.min(viewport.fieldOfViewDeg, 82),
  });
}

export function interpolateViewport(
  from: SkyViewportState,
  to: SkyViewportState,
  amount: number,
): SkyViewportState {
  const eased = clamp(amount, 0, 1);
  return normalizeViewport({
    centerAzimuthDeg: from.centerAzimuthDeg + shortestAzimuthDelta(from.centerAzimuthDeg, to.centerAzimuthDeg) * eased,
    centerAltitudeDeg: from.centerAltitudeDeg + (to.centerAltitudeDeg - from.centerAltitudeDeg) * eased,
    fieldOfViewDeg: from.fieldOfViewDeg + (to.fieldOfViewDeg - from.fieldOfViewDeg) * eased,
  });
}

export function normalizeViewport(viewport: SkyViewportState): SkyViewportState {
  return {
    centerAzimuthDeg: normalizeDegrees(viewport.centerAzimuthDeg),
    centerAltitudeDeg: clamp(viewport.centerAltitudeDeg, MinCenterAltitudeDeg, MaxCenterAltitudeDeg),
    fieldOfViewDeg: clamp(viewport.fieldOfViewDeg, MinFieldOfViewDeg, MaxFieldOfViewDeg),
  };
}

function shortestAzimuthDelta(from: number, to: number): number {
  return ((to - from + 540) % 360) - 180;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
